"use strict";
/**
 * Settings Display UI
 * Handles settings values display and slippage slider
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateVersionDisplay = updateVersionDisplay;
exports.updateSettingsDisplay = updateSettingsDisplay;
exports.updateInningVisibility = updateInningVisibility;
exports.updateInningDisplay = updateInningDisplay;
exports.updateSlippageSlider = updateSlippageSlider;
exports.updateIndependentSlippageSlider = updateIndependentSlippageSlider;
exports.updateIndependentSlippageVisibility = updateIndependentSlippageVisibility;
exports.updateManualPriceSlider = updateManualPriceSlider;
exports.updateSlippageVisibility = updateSlippageVisibility;
exports.updateManualPriceVisibility = updateManualPriceVisibility;
exports.updateSellModeIndicator = updateSellModeIndicator;
const version_1 = require("../version");
const sport_mode_1 = require("./sport-mode");
const event_type_service_1 = require("../services/event-type-service");
/**
 * Update app version display in settings modal
 */
function updateVersionDisplay() {
    const versionElement = document.getElementById("app-version");
    if (versionElement) {
        versionElement.textContent = `v${version_1.VERSION}`;
    }
}
/**
 * Update settings display
 */
function updateSettingsDisplay(settings) {
    // Shares / slippage / timeout readouts were removed from the settings-info
    // popup — shares is an inactive setting, timeout lives in the settings
    // modal, and slippage has its own on-page slider.
    const dollarsDisplay = document.getElementById("dollars-display");
    const primaryOrdersToggle = document.getElementById("primary-orders-toggle");
    const closeOppositeToggle = document.getElementById("close-opposite-toggle");
    const primaryOrdersEnabled = settings.primaryOrdersEnabled !== false;
    const closeOppositeOnBuy = settings.closeOppositeOnBuy !== false;
    if (dollarsDisplay)
        dollarsDisplay.textContent = `$${settings.dollarAmount.toString()}`;
    if (primaryOrdersToggle) {
        primaryOrdersToggle.classList.toggle("trade-setting-toggle-disabled", !primaryOrdersEnabled);
        primaryOrdersToggle.setAttribute("aria-pressed", primaryOrdersEnabled ? "true" : "false");
        primaryOrdersToggle.title = primaryOrdersEnabled
            ? "Primary market orders enabled"
            : "Primary market orders disabled";
        primaryOrdersToggle.setAttribute("aria-label", primaryOrdersEnabled
            ? "Disable primary market orders"
            : "Enable primary market orders");
    }
    if (closeOppositeToggle) {
        closeOppositeToggle.classList.toggle("trade-setting-toggle-disabled", !closeOppositeOnBuy);
        closeOppositeToggle.setAttribute("aria-pressed", closeOppositeOnBuy ? "true" : "false");
        closeOppositeToggle.title = closeOppositeOnBuy
            ? "Close opposite-side position on buy orders"
            : "Keep opposite-side position open on buy orders";
        closeOppositeToggle.setAttribute("aria-label", closeOppositeOnBuy
            ? "Disable opposite-position closeout on buy orders"
            : "Enable opposite-position closeout on buy orders");
    }
}
/** Show or hide the half-inning strip (baseball events only). */
function updateInningVisibility(visible) {
    const container = document.getElementById("inning-container");
    if (container) {
        container.classList.toggle("hidden", !visible);
    }
}
/**
 * Update the inning number, and enable the step-back control only when an
 * earlier half-inning exists. The half is a required argument so a new caller
 * cannot render the clock while leaving ◀½ enabled at the top of the 1st.
 */
function updateInningDisplay(inning, halfInning) {
    const inn = document.getElementById("inning-number");
    if (inn)
        inn.textContent = inning.toString();
    const back = document.getElementById("prev-half-inning-btn");
    if (back)
        back.disabled = (0, event_type_service_1.previousHalfInning)({ inning, halfInning }) === null;
}
/**
 * Update the slippage slider value and display
 */
function updateSlippageSlider(slippagePercent) {
    const slider = document.getElementById("slippage-slider");
    const display = document.getElementById("slippage-value-display");
    if (slider) {
        slider.value = Math.round(slippagePercent).toString();
    }
    if (display) {
        display.textContent = `${Math.round(slippagePercent)}%`;
    }
}
/**
 * Update an independent side slippage slider value and display
 */
function updateIndependentSlippageSlider(side, slippagePercent) {
    const slider = document.getElementById(`slippage-slider-${side}`);
    const display = document.getElementById(`slippage-value-display-${side}`);
    if (slider) {
        slider.value = Math.round(slippagePercent).toString();
    }
    if (display) {
        display.textContent = `${Math.round(slippagePercent)}%`;
    }
}
/**
 * Show or hide independent slippage containers based on toggle state
 */
function updateIndependentSlippageVisibility(isIndependent) {
    const singleContainer = document.getElementById("slippage-slider-container");
    const independentContainer = document.getElementById("independent-slippage-container");
    if (singleContainer) {
        if (isIndependent) {
            singleContainer.classList.add("hidden");
        }
        else {
            singleContainer.classList.remove("hidden");
        }
    }
    if (independentContainer) {
        if (isIndependent) {
            independentContainer.classList.remove("hidden");
        }
        else {
            independentContainer.classList.add("hidden");
        }
    }
}
/**
 * Update the manual price slider, numeric input, and selected/unselected state.
 */
function updateManualPriceSlider(side, price, isSelected = true) {
    const sliderId = side === "yes" ? "manual-yes-price-slider" : "manual-no-price-slider";
    const inputId = side === "yes" ? "manual-yes-price-input" : "manual-no-price-input";
    const alertId = side === "yes" ? "manual-yes-price-alert" : "manual-no-price-alert";
    const sideCardId = side === "yes" ? "manual-price-side-yes" : "manual-price-side-no";
    const slider = document.getElementById(sliderId);
    const numInput = document.getElementById(inputId);
    const alert = document.getElementById(alertId);
    const sideCard = document.getElementById(sideCardId);
    const rounded = Math.round(price);
    if (slider) {
        slider.value = rounded.toString();
    }
    if (numInput) {
        numInput.value = isSelected ? rounded.toString() : "";
        numInput.placeholder = "--";
    }
    if (alert) {
        alert.classList.toggle("hidden", isSelected);
    }
    if (sideCard) {
        sideCard.classList.toggle("manual-price-side-unset", !isSelected);
    }
}
/**
 * Centralized function to update slippage bar visibility.
 * Hides slippage controls when manual price mode OR a sport button layout is
 * active (under `forceDefaultButtons` the traditional layout shows, so the
 * slippage controls come back). This should be called any time either mode
 * changes to ensure consistency.
 */
function updateSlippageVisibility(settings) {
    const slippageContainer = document.getElementById("slippage-slider-container");
    const independentContainer = document.getElementById("independent-slippage-container");
    const isManual = settings.manualPriceMode === true;
    const shouldHideSlippage = isManual || (0, sport_mode_1.isSportLayoutActive)(settings);
    if (shouldHideSlippage) {
        if (slippageContainer) {
            slippageContainer.classList.add("hidden");
        }
        if (independentContainer) {
            independentContainer.classList.add("hidden");
        }
    }
    else {
        // Restore based on independent slippage setting
        const isIndependent = settings.independentSlippage === true;
        if (slippageContainer) {
            slippageContainer.classList.toggle("hidden", isIndependent);
        }
        if (independentContainer) {
            independentContainer.classList.toggle("hidden", !isIndependent);
        }
    }
}
/**
 * Show or hide the manual price container based on manual price mode
 */
function updateManualPriceVisibility(isManualMode) {
    const container = document.getElementById("manual-price-container");
    if (container) {
        container.classList.toggle("hidden", !isManualMode);
    }
}
/**
 * Update sell button badges to reflect the current sell mode.
 * In "partial" mode, shows a badge with the fixed quantity (e.g., "×5").
 * In "full" mode, hides the badges.
 */
function updateSellModeIndicator(sellMode, fixedSellQuantity) {
    const badgeA = document.getElementById("sell-badge-a");
    const badgeB = document.getElementById("sell-badge-b");
    const baseballBadgeA = document.getElementById("bb-sell-badge-a");
    const baseballBadgeB = document.getElementById("bb-sell-badge-b");
    const basketballBadgeA = document.getElementById("bk-sell-badge-a");
    const basketballBadgeB = document.getElementById("bk-sell-badge-b");
    const isPartial = sellMode === "partial";
    const label = `×${fixedSellQuantity}`;
    [
        badgeA,
        badgeB,
        baseballBadgeA,
        baseballBadgeB,
        basketballBadgeA,
        basketballBadgeB,
    ].forEach((badge) => {
        if (!badge)
            return;
        if (isPartial) {
            badge.textContent = label;
            badge.classList.remove("hidden");
        }
        else {
            badge.textContent = "";
            badge.classList.add("hidden");
        }
    });
}
